import { LoadCheckoutWebComponents, WindowCheckoutWebComponents } from './types';

export * from './types';
export const CDN_URL = 'https://checkout-web-components.checkout.com/index.js';

const THIRTY_SECONDS = 30_000;

export enum CheckoutLoaderErrorType {
  Integration = 'Integration',
}

export enum CheckoutLoaderIntegrationErrorCode {
  BrowserEnvironmentRequired = 'browser_environment_required',
  CheckoutWebComponentInstanceNotOnWindow = 'checkout_web_component_instance_not_on_window',
  ScriptFailed = 'script_failed',
  ScriptTimedOut = 'script_timed_out',
}

type CheckoutLoaderErrorOptions = {
  type: CheckoutLoaderErrorType;
  code: CheckoutLoaderIntegrationErrorCode;
  message: string;
};

export class CheckoutLoaderError extends Error {
  public type: CheckoutLoaderErrorType;
  public code: CheckoutLoaderIntegrationErrorCode;

  constructor({ type, message, code }: CheckoutLoaderErrorOptions) {
    super(`[${type}]: ${message} [${code}]`);

    Object.setPrototypeOf(this, CheckoutLoaderError.prototype);

    this.type = type;
    this.code = code;
    this.name = 'CheckoutLoaderError';
  }
}

// Create a script tag with the CDN URL and inject it into the head of the document.
const createScript = (url: string) => {
  const existingScripts = document.querySelectorAll<HTMLScriptElement>(`script[src^="${CDN_URL}"]`);

  if (existingScripts.length > 0) {
    // if the scripts already exist, remove them and create a new one.
    for (let i = 0; i < existingScripts.length; i++) {
      const script = existingScripts[i];

      script.remove();
    }
  }

  const script = document.createElement('script');
  script.src = url;
  script.async = true;

  document.head.appendChild(script);

  return script;
};

const waitForScriptLoad = async (script: HTMLScriptElement) => {
  return new Promise<WindowCheckoutWebComponents>((resolve, reject) => {
    script.addEventListener('load', () => {
      if (window.CheckoutWebComponents) {
        resolve(window.CheckoutWebComponents);
      } else {
        reject(
          new CheckoutLoaderError({
            type: CheckoutLoaderErrorType.Integration,
            message: "CheckoutWebComponents loaded but window.CheckoutWebComponents isn't present",
            code: CheckoutLoaderIntegrationErrorCode.CheckoutWebComponentInstanceNotOnWindow,
          })
        );
      }
    });

    script.addEventListener('error', () => {
      reject(
        new CheckoutLoaderError({
          type: CheckoutLoaderErrorType.Integration,
          message: 'CheckoutWebComponents did not load due to script failing',
          code: CheckoutLoaderIntegrationErrorCode.ScriptFailed,
        })
      );
    });

    setTimeout(() => {
      reject(
        new CheckoutLoaderError({
          type: CheckoutLoaderErrorType.Integration,
          message: 'CheckoutWebComponents did not load due to script timing out',
          code: CheckoutLoaderIntegrationErrorCode.ScriptTimedOut,
        })
      );
    }, THIRTY_SECONDS);
  });
};

/**
 * Loads the checkout web components.
 * @throws {CheckoutLoaderError}
 */
export const loadCheckoutWebComponents: LoadCheckoutWebComponents = async (options) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    throw new CheckoutLoaderError({
      type: CheckoutLoaderErrorType.Integration,
      message: 'CheckoutWebComponents can only run in a browser environment',
      code: CheckoutLoaderIntegrationErrorCode.BrowserEnvironmentRequired,
    });
  }

  // if the script is already loaded, return the CheckoutWebComponents from the window object.
  if (window.CheckoutWebComponents) {
    return window.CheckoutWebComponents(options);
  }

  const script = createScript(CDN_URL);

  const CheckoutWebComponents = await waitForScriptLoad(script);

  return CheckoutWebComponents(options);
};
